import clsx from 'clsx';
import React, {FC, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {DocToc, PageHero, PageShell, PageTitle, Prose, Section} from 'components';

import EnExtension from './content/extension/en';
import KoExtension from './content/extension/ko';
import RuExtension from './content/extension/ru';
import ZhExtension from './content/extension/zh';
import EnFusion from './content/fusion/en';
import KoFusion from './content/fusion/ko';
import RuFusion from './content/fusion/ru';
import ZhFusion from './content/fusion/zh';
import EnImage from './content/image/en';
import KoImage from './content/image/ko';
import RuImage from './content/image/ru';
import ZhImage from './content/image/zh';
import EnListing from './content/listing/en';
import KoListing from './content/listing/ko';
import RuListing from './content/listing/ru';
import ZhListing from './content/listing/zh';
import EnVideo from './content/video/en';
import KoVideo from './content/video/ko';
import RuVideo from './content/video/ru';
import ZhVideo from './content/video/zh';

/** 使用指南（i18n 壳 + 五 tab）：视频翻译 / 图片翻译 / 主图详情图 / 模特场景图 /
 * 浏览器插件（标签文案走 docs 词典）。2026-09-17 前四个 tab 中 video/image/
 * extension/fusion 按业务最新版 docx 指南重写（截图 webp 本地化于 assets/v3/guide/，
 * fusion tab 合并场景图+模特图两份指南）；listing tab 仍为 2026-09-16 按产品页文案
 * 组织的图文说明（业务暂未提供独立指南文档）。
 * tab 用 useState 切换；支持 ?tab=video|image|listing|fusion|extension 指定初始
 * tab（浏览器插件落地页的「离线安装」入口直达插件 tab）。h1 在壳 hero，内容组件从
 * h2 起。v3 改版：soft hero + 药丸 tab + Prose 正文（语义标签，样式见
 * styles/v3/prose.css），不再复用 ApiDoc.scss。 */
type GuideTabKey = 'extension' | 'fusion' | 'image' | 'listing' | 'video';

/** 从 URL query 读取初始 tab（仅合法值生效，默认 video） */
const initialTab = (): GuideTabKey => {
  const tab = new URLSearchParams(window.location.search).get('tab');
  return tab === 'extension' || tab === 'image' || tab === 'listing' || tab === 'fusion' ? tab : 'video';
};

const Guide: FC = () => {
  const {i18n, t} = useTranslation('docs');
  const [activeTab, setActiveTab] = useState<GuideTabKey>(initialTab);
  const lang = i18n.language;

  /** 按语言选内容组件（zh 兜底：zh-CN 与未匹配语言走中文） */
  const renderContent = (en: FC, ko: FC, ru: FC, zh: FC) => {
    const Cmp = ({en, ko, ru} as Record<string, FC>)[lang] ?? zh;
    return <Cmp />;
  };

  const content = {
    extension: renderContent(EnExtension, KoExtension, RuExtension, ZhExtension),
    fusion: renderContent(EnFusion, KoFusion, RuFusion, ZhFusion),
    image: renderContent(EnImage, KoImage, RuImage, ZhImage),
    listing: renderContent(EnListing, KoListing, RuListing, ZhListing),
    video: renderContent(EnVideo, KoVideo, RuVideo, ZhVideo),
  }[activeTab];

  const tabs: {key: GuideTabKey; label: string}[] = [
    {key: 'video', label: t('guide.tabs.video')},
    {key: 'image', label: t('guide.tabs.image')},
    {key: 'listing', label: t('guide.tabs.listing')},
    {key: 'fusion', label: t('guide.tabs.fusion')},
    {key: 'extension', label: t('guide.tabs.extension')},
  ];

  return (
    <PageShell>
      <PageTitle canonicalUrl="https://www.xiangjifanyi.com/home/guide" title={t('guide.title')} />
      <PageHero badge={t('guide.badge')} description={t('guide.desc')} title={t('guide.title')} variant="soft" />
      <Section compact>
        <div className="flex flex-wrap justify-center gap-2 mb-10" role="tablist">
          {tabs.map((tab) => (
            <button
              aria-selected={activeTab === tab.key}
              className={clsx(
                'px-5 py-2.5 rounded-full text-sm lg:text-base font-medium transition-colors',
                activeTab === tab.key
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
              )}
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="max-w-[79rem] mx-auto xl:grid xl:grid-cols-[12.5rem_minmax(0,48rem)_12.5rem] xl:gap-12">
          {/* 左占位列：与目录等宽，正文保持页面正中（目录仅 xl+ 显示） */}
          <div aria-hidden="true" className="hidden xl:block" />
          <Prose>{content}</Prose>
          {/* key=tab：切换 tab 时重挂载，目录按新内容重建；指南正文章节在 h3，depth=3 */}
          <DocToc className="hidden xl:block" depth={3} key={activeTab} />
        </div>
      </Section>
    </PageShell>
  );
};

export default Guide;
